import React from 'react';
import { Youtube, Apple, Instagram, Play } from 'lucide-react';
import { MUSIC_PAGE, SOCIALS, SEO_META } from '../mock';
import { SpotifyIcon } from '../components/icons';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';

const PlatformButton = ({ href, icon, label, dark = false }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`group inline-flex items-center gap-2 border px-4 py-2.5 text-[10px] tracking-[0.18em] transition-colors duration-300 ${
      dark
        ? 'border-white/20 bg-transparent text-neutral-200 hover:border-gold hover:text-gold'
        : 'border-ink/15 bg-white text-ink hover:border-gold-dark hover:bg-cream'
    }`}
  >
    {icon}
    {label}
  </a>
);

const MiniLink = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    onClick={(e) => e.stopPropagation()}
    className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-neutral-500 transition-colors duration-300 hover:border-gold hover:text-gold"
  >
    {children}
  </a>
);

const ReleaseCard = ({ release, onPlay, delay }) => (
  <Reveal delay={delay}>
    <div className="group flex flex-col items-center text-center">
      {/* Cover */}
      <button
        onClick={() => onPlay(release)}
        aria-label={`Play ${release.title} ${release.version}`}
        className="relative block aspect-square w-full overflow-hidden shadow-[0_25px_50px_-20px_rgba(12,11,10,0.4)]"
      >
        <img
          src={release.cover}
          alt={`${release.title} — ${release.version}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        {/* Hover play */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-400 group-hover:opacity-100">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-ink shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110">
            <Play size={20} fill="currentColor" />
          </span>
        </div>
      </button>

      {/* Title */}
      <h3 className="mt-7 font-serif text-3xl font-light tracking-[0.08em] text-ink">{release.title}</h3>
      <p className="mt-2 text-[10px] tracking-[0.3em] text-gold-dark">{release.version}</p>
      <div className="mt-4 h-px w-10 bg-gold/60" />
      <p className="mt-4 text-base text-ink/60">{release.credit}</p>

      {/* Platform buttons */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
        <PlatformButton
          href={release.youtube}
          label="YOUTUBE"
          icon={<Youtube size={13} className="text-[#FF0000]" />}
        />
        <PlatformButton
          href={release.spotify}
          label="SPOTIFY"
          icon={<SpotifyIcon size={12} className="text-[#1DB954]" />}
        />
        <PlatformButton
          href={release.apple}
          label="APPLE MUSIC"
          icon={<Apple size={13} className="text-ink" />}
        />
      </div>
    </div>
  </Reveal>
);

const SocialCircle = ({ id, url, label }) => {
  let icon;
  if (id === 'instagram') icon = <Instagram size={15} strokeWidth={1.5} />;
  else if (id === 'youtube') icon = <Youtube size={15} strokeWidth={1.5} />;
  else if (id === 'apple') icon = <Apple size={15} />;
  else icon = <SpotifyIcon size={14} />;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-neutral-300 transition-colors duration-300 hover:border-gold hover:text-gold"
    >
      {icon}
    </a>
  );
};

const MusicPage = ({ onPlay }) => {
  const { hero, releases, album, connect } = MUSIC_PAGE;
  const connectSocials = SOCIALS;

  return (
    <main>
      <Seo {...SEO_META.music} />
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink pt-20">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
          <img
            src={hero.image}
            alt="Kanwal H. Malik singing"
            className="h-full w-full object-cover object-[50%_18%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-ink/30 lg:hidden" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 py-16 md:px-10 md:py-24">
          <div className="max-w-xl">
            <Reveal>
              <p className="mb-5 flex items-center gap-4 text-[10px] uppercase tracking-[0.5em] text-gold">
                {hero.label}
                <span className="h-px w-14 bg-gold/50" />
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-[0.02em] text-white md:text-7xl">
                {hero.titleLines[0]}
              </h1>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-8 space-y-1 text-lg leading-relaxed text-neutral-300 md:text-xl">
                {hero.text.map((line, i) => (
                  <p key={line} className="font-serif font-light">
                    {line}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Releases */}
      <section className="bg-[#faf7f0] py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-12 text-center">
            <Reveal>
              <p className="mb-4 text-[10px] uppercase tracking-[0.5em] text-gold-dark">Music</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif text-4xl font-light tracking-[0.04em] text-ink md:text-5xl">
                Latest Releases
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <div className="mx-auto mt-5 h-px w-12 bg-gold" />
            </Reveal>
          </div>
          <div className="grid gap-x-8 gap-y-12 lg:grid-cols-3">
            {releases.map((release, i) => (
              <ReleaseCard key={release.id} release={release} onPlay={onPlay} delay={i * 130} />
            ))}
          </div>
        </div>
      </section>

      {/* MANIFEST — full album */}
      <section className="border-t border-white/5 bg-ink py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div className="grid items-center gap-10 md:grid-cols-[230px_1fr] lg:gap-16">
            <Reveal>
              <img
                src={album.cover}
                alt="MANIFEST — album by KHM"
                loading="lazy"
                className="aspect-square w-full max-w-[260px] object-cover shadow-[0_30px_70px_-20px_rgba(0,0,0,0.8)]"
              />
            </Reveal>
            <div>
              <Reveal>
                <p className="mb-4 text-[10px] uppercase tracking-[0.5em] text-gold">{album.label}</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="font-serif text-5xl font-light tracking-[0.08em] text-white md:text-6xl">
                  {album.title}
                </h2>
              </Reveal>
              <Reveal delay={220}>
                <p className="mt-4 text-[10px] tracking-[0.3em] text-neutral-400">{album.meta}</p>
              </Reveal>
              <Reveal delay={320}>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  <PlatformButton
                    dark
                    href={album.youtube}
                    label="YOUTUBE"
                    icon={<Youtube size={13} className="text-[#FF0000]" />}
                  />
                  <PlatformButton
                    dark
                    href={album.spotify}
                    label="SPOTIFY"
                    icon={<SpotifyIcon size={12} className="text-[#1DB954]" />}
                  />
                  <PlatformButton
                    dark
                    href={album.apple}
                    label="APPLE MUSIC"
                    icon={<Apple size={13} />}
                  />
                </div>
              </Reveal>
            </div>
          </div>

          {/* Tracklist */}
          <Reveal delay={200}>
            <div className="mt-12 border-t border-white/10">
              {album.tracks.map((track) => (
                <div
                  key={track.id}
                  className="group flex items-center gap-4 border-b border-white/10 py-4 transition-colors duration-300 hover:bg-white/[0.03] md:gap-6"
                >
                  <span className="w-8 text-right font-serif text-base italic text-neutral-600">
                    {String(track.num).padStart(2, '0')}
                  </span>
                  <button
                    onClick={() => onPlay(track)}
                    aria-label={`Play ${track.title}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink"
                  >
                    <Play size={12} fill="currentColor" />
                  </button>
                  <button
                    onClick={() => onPlay(track)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block truncate text-base tracking-[0.1em] text-neutral-100 transition-colors duration-300 group-hover:text-gold">
                      {track.title}
                    </span>
                    <span className="block text-[10px] tracking-[0.1em] text-neutral-500">KHM</span>
                  </button>
                  <div className="hidden items-center gap-2 sm:flex">
                    <MiniLink href={track.spotify} label="Listen on Spotify">
                      <SpotifyIcon size={11} />
                    </MiniLink>
                    <MiniLink href={track.apple} label="Listen on Apple Music">
                      <Apple size={12} />
                    </MiniLink>
                    <MiniLink href={track.youtube} label="Watch on YouTube">
                      <Youtube size={12} />
                    </MiniLink>
                  </div>
                  <span className="w-10 text-right text-[10px] tracking-[0.12em] text-neutral-500">
                    {track.duration}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stay connected */}
      <section className="relative overflow-hidden bg-ink py-14 md:py-16">
        <div className="absolute inset-0">
          <img
            src={connect.image}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover opacity-[0.14]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="relative z-10 mx-auto max-w-xl px-6 text-center">
          <Reveal>
            <p className="mb-5 text-[11px] uppercase tracking-[0.5em] text-gold">{connect.label}</p>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-base leading-relaxed text-neutral-300">{connect.text}</p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex items-center justify-center gap-4">
              {connectSocials.map((s) => (
                <SocialCircle key={s.id} {...s} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default MusicPage;
